/**
 * Board search — measured in round 5o (`specs/M1-B6-filters-sort.md` §4.8,
 * `discovery/ui-reference/filters/sandbox-search-typed.png`).
 *
 * It is **not a pane**. The `Search` toolbar control grows in place from 91 px to
 * **266 × 32**, holding a 163 × 28 input with the placeholder `Search this board`, an `×`
 * and a search-options affordance; the controls to its right slide along. This component is
 * therefore rendered *inside* the toolbar, not in the pane layer.
 *
 * The term filters on the item name and any visible text cell, debounced 250 ms, is combined
 * with the active filter by AND, and is **never** saved to the view — it lives in the personal
 * state only, which `sharedSettings()` strips on save.
 */
import { useEffect, useRef, useState } from 'react';
import { useTable, useTableActions } from '../context';

export const SEARCH_DEBOUNCE_MS = 250;

export function SearchPane() {
  const effective = useTable((state) => state.effective);
  const payload = useTable((state) => state.payload);
  const actions = useTableActions();
  const [term, setTerm] = useState(effective.search);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (term !== actions.effective.search) actions.setEffective({ search: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [term, actions]);

  return (
    <div className="ez-search-box" role="search" data-testid="search-pane">
      <input
        className="ez-search-box__input"
        type="search"

        autoFocus
        aria-label="Search this board"
        placeholder="Search this board"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Escape') {
            setTerm('');
            actions.setEffective({ search: '' });
            actions.openPane(null);
          }
        }}
      />
      {effective.search ? (
        <span className="ez-search-box__count" data-testid="search-count">
          {`${payload?.totals.filtered ?? 0} of ${payload?.totals.items ?? 0}`}
        </span>
      ) : null}
      <button
        type="button"
        aria-label="Close search"
        className="ez-search-box__close"
        onClick={() => {
          setTerm('');
          actions.setEffective({ search: '' });
          actions.openPane(null);
        }}
      >
        ×
      </button>
    </div>
  );
}
